/**
 * Klantvlak (wit) in de zaak. Sinds motor 7 golf 2 sessie E de klantkaart
 * v10 (Brain grote-ronde/75, keuzes 11 tot en met 18): kop met naam,
 * adres en AVG, knoptegels Mail, Bel, WhatsApp en Lightspeed, cijfers 2x2
 * en de tabs Dier, Bestelling, Retour, Contact en Reviews
 * (`./kaart10/KlantkaartV10`). Dezelfde kaart staat op `/klanten/:id`, uit
 * dezelfde cache (`v6Sleutels.klantKaart`).
 *
 * Wat dit vlak zelf houdt:
 * - een koppelvoorstel direct onder de tegels, met "niet deze klant";
 * - bij een gekoppelde mailzaak "niet deze klant" (losmaken) in de tab
 *   Contact;
 * - zonder klant op de zaak (ook een belzaak zonder e-mail, ook als de
 *   zaak afgerond is): "Klant koppelen" met zoeken en één bevestiging.
 *
 * Mail en WhatsApp zetten een nieuwe zaak klaar in dat kanaal (sessie 72);
 * er wordt nooit iets verstuurd. Lightspeed-gegevens wijzigen hier nooit.
 *
 * Sessie 75: het vlak blijft staan bij een andere zaak. De kaart hangt aan
 * het klant-id (zelfde klant, geen nieuw verzoek); fouten en vragen van de
 * vorige zaak worden gericht teruggezet.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";

import { v6Api, type KoppelBanner, type V6Zaak } from "../api";
import { useV6 } from "../context";
import { Icoon } from "../Icoon";
import { KlantKoppelVlak, type Gekoppeld } from "../KlantKoppelVlak";
import type { KlantKaart } from "../Klanten/types";
import { useNieuwBericht } from "../nieuwBericht";
import { v6Sleutels } from "../sleutels";
import { KlantkaartV10 } from "./kaart10/KlantkaartV10";
import { matchWoord } from "./opmaak";

export function Klantvlak({
  zaakId,
  zaak,
  zaakLaadt,
  zaakVoorlopig = false,
  zaakFout = false,
  verborgen,
}: {
  zaakId: string | null;
  zaak: V6Zaak | undefined;
  zaakLaadt: boolean;
  /** De zaak komt nog uit de lijstkaart (sessie 75). */
  zaakVoorlopig?: boolean;
  /** De zaak zelf kon niet geladen worden. */
  zaakFout?: boolean;
  verborgen: boolean;
}) {
  const { sluitRechts } = useV6();

  // Geen klant op de zaak: kijk of er een koppelvoorstel is.
  const zoekVoorstel = Boolean(zaakId && zaak && !zaakVoorlopig && !zaak.klant_id);
  const koppeling = useQuery({
    queryKey: ["v6", "koppeling", zaakId ?? ""],
    queryFn: () => v6Api.koppeling(zaakId as string),
    enabled: zoekVoorstel,
  });
  const voorstel = zoekVoorstel ? (koppeling.data?.voorstellen[0] ?? null) : null;
  const klantId = zaak?.klant_id ?? voorstel?.klant_id ?? null;

  // Sessie 75: zolang de volgende zaak laadt blijven kaart en orders op de
  // vorige klant staan (niet getoond; de kop zegt dan "laden"). Is het
  // dezelfde klant, dan wisselt de sleutel niet en komt er geen verzoek.
  const [vorigeKlant, setVorigeKlant] = useState<string | null>(klantId);
  if (klantId && klantId !== vorigeKlant) setVorigeKlant(klantId);
  const zaakOnderweg = zaakLaadt || zaakVoorlopig || (zoekVoorstel && koppeling.isLoading);
  const vraagKlant = klantId ?? (zaakOnderweg ? vorigeKlant : null);

  // Sessie 73: één cache met de pagina Klanten.
  const kaart = useQuery({
    queryKey: v6Sleutels.klantKaart(vraagKlant ?? ""),
    queryFn: () => v6Api.klantKaart(vraagKlant as string, "zaak"),
    enabled: Boolean(vraagKlant),
  });

  const sluitKnop = (
    <button
      type="button"
      className="sluitvlak"
      aria-label="Sluiten"
      title="Sluiten"
      onClick={sluitRechts}
    >
      <Icoon naam="x" klein />
    </button>
  );

  let inhoud: ReactNode;
  if (!zaakId) {
    inhoud = <LegeKop tekst="Geen zaak gekozen." sluitKnop={sluitKnop} />;
  } else if (
    zaakLaadt ||
    (zaakVoorlopig && !klantId) ||
    (zoekVoorstel && koppeling.isLoading) ||
    kaart.isLoading
  ) {
    inhoud = <LegeKop tekst="Klant wordt geladen." sluitKnop={sluitKnop} />;
  } else if (zaakFout && !zaak) {
    inhoud = (
      <>
        <LegeKop tekst="" sluitKnop={sluitKnop} />
        <p className="v6-fout belfout" role="alert">
          De zaak kon niet geladen worden.
        </p>
      </>
    );
  } else if (zoekVoorstel && koppeling.isError) {
    inhoud = (
      <>
        <LegeKop tekst="" sluitKnop={sluitKnop} />
        <p className="v6-fout belfout" role="alert">
          De koppeling met een klant kon niet opgehaald worden.
        </p>
      </>
    );
  } else if (!klantId) {
    // Sessie 73 blok 4: ook bij een afgeronde zaak en een belzaak zonder
    // e-mail; alleen de zaak zelf telt, niet zijn kanaal of stand.
    inhoud = (
      <>
        <LegeKop tekst="Geen klant gekoppeld." sluitKnop={sluitKnop} />
        {zaak ? <KlantKoppelen key={zaakId} zaakId={zaakId} /> : null}
      </>
    );
  } else if (kaart.isError || !kaart.data) {
    inhoud = (
      <>
        <LegeKop tekst="" sluitKnop={sluitKnop} />
        <p className="v6-fout belfout" role="alert">
          De klant kon niet geladen worden.
        </p>
      </>
    );
  } else {
    inhoud = (
      <KlantInhoud
        zaakId={zaakId}
        zaak={zaak}
        kaart={kaart.data}
        voorstel={voorstel}
        sluitKnop={sluitKnop}
      />
    );
  }

  return (
    <section
      className="v6-vlak v6-klantvlak"
      aria-label="Klant"
      aria-hidden={verborgen || undefined}
    >
      {inhoud}
    </section>
  );
}

function LegeKop({ tekst, sluitKnop }: { tekst: string; sluitKnop: ReactNode }) {
  return (
    <>
      <div className="rk">
        <span className="wie">
          <b>Klant</b>
        </span>
        <span style={{ marginLeft: "auto" }} />
        {sluitKnop}
      </div>
      {tekst ? <p className="v6-leeg">{tekst}</p> : null}
    </>
  );
}

type Voorstel = KoppelBanner["voorstellen"][number];

function KlantInhoud({
  zaakId,
  zaak,
  kaart,
  voorstel,
  sluitKnop,
}: {
  zaakId: string;
  zaak: V6Zaak | undefined;
  kaart: KlantKaart;
  voorstel: Voorstel | null;
  sluitKnop: ReactNode;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const nieuw = useNieuwBericht("klantblok");

  // Sessie 75: alleen wat de koppeling raakt, niet alles onder ["v6"]:
  // deze zaak, het voorstel, de klantkaart (met tijdlijn) en de lijst (de
  // naam op de kaart).
  const ververs = (id: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: v6Sleutels.zaak(id) }),
      queryClient.invalidateQueries({ queryKey: ["v6", "koppeling", id] }),
      queryClient.invalidateQueries({ queryKey: v6Sleutels.klantKaart(kaart.id) }),
      queryClient.invalidateQueries({ queryKey: v6Sleutels.lijst }),
    ]);
  const nietDeze = useMutation({
    mutationFn: (v: { zaakId: string; voorstelId: string }) =>
      v6Api.nietDezeKlant(v.zaakId, v.voorstelId),
    onSuccess: (_uit, v) => ververs(v.zaakId),
  });
  // Sessie 70, punt 9: "niet deze klant" ook bij een al gekoppelde klant.
  // De vraag "losmaken?" hoort bij de zaak waarop hij gesteld is; bij een
  // andere zaak staat hij vanzelf niet meer open.
  const [losVraagZaak, setLosVraagZaak] = useState<string | null>(null);
  const losVraag = losVraagZaak === zaakId;
  const setLosVraag = (aan: boolean) => setLosVraagZaak(aan ? zaakId : null);
  const losmaken = useMutation({
    mutationFn: (id: string) => v6Api.klantLosmaken(id),
    onSuccess: (_uit, id) => {
      setLosVraagZaak(null);
      return ververs(id);
    },
  });
  // Nieuw bericht: de fout hoort bij de zaak waarop hij ontstond.
  const [nieuwZaak, setNieuwZaak] = useState<string | null>(null);
  const nieuwFout = nieuwZaak === zaakId ? nieuw.fout : null;
  const startNieuw = (kanaal: "mail" | "whatsapp") => {
    setNieuwZaak(zaakId);
    void nieuw.start(kaart.id, kanaal).catch(() => undefined);
  };

  // Andere zaak: fouten en de open vraag van de vorige zaak weg.
  const { reset: resetNietDeze } = nietDeze;
  const { reset: resetLosmaken } = losmaken;
  useEffect(() => {
    resetNietDeze();
    resetLosmaken();
  }, [zaakId, resetNietDeze, resetLosmaken]);

  // Waarop gekoppeld komt alleen van de server; zonder die regel zeggen
  // we niets.
  const gekoppeldOp = zaak?.klantkaart?.gekoppeld_op || null;

  const tussen = (
    <>
      {nieuwFout ? (
        <p className="v6-fout belfout" role="alert">
          Kon geen bericht klaarzetten: {nieuwFout}
        </p>
      ) : null}
      {voorstel ? (
        <div className="koppel koppelrij">
          {`voorstel op ${matchWoord(voorstel.match_type)}`}
          <button
            type="button"
            disabled={nietDeze.isPending}
            onClick={() => nietDeze.mutate({ zaakId, voorstelId: voorstel.voorstel_id })}
          >
            niet deze klant
          </button>
        </div>
      ) : null}
      {nietDeze.isError ? (
        <p className="v6-fout belfout" role="alert">
          Loskoppelen lukte niet. Probeer het opnieuw.
        </p>
      ) : null}
    </>
  );

  // Alleen bij mail: chat, WhatsApp en telefoon koppelen op nummer of
  // account (recensie sessie 70).
  const contactExtra =
    !voorstel && zaak?.klant_id && zaak.kanaal === "mail" ? (
      <>
        <div className="koppel">
          {gekoppeldOp ? `gekoppeld op ${gekoppeldOp}` : "gekoppeld"}
          {losVraag ? (
            <>
              <span>Klant losmaken van deze zaak?</span>
              <button
                type="button"
                disabled={losmaken.isPending}
                onClick={() => losmaken.mutate(zaakId)}
              >
                losmaken
              </button>
              <button type="button" onClick={() => setLosVraag(false)}>
                nee
              </button>
            </>
          ) : (
            <button type="button" onClick={() => setLosVraag(true)}>
              niet deze klant
            </button>
          )}
        </div>
        {losmaken.isError ? (
          <p className="v6-fout" role="alert">
            Losmaken lukte niet. Probeer het opnieuw.
          </p>
        ) : null}
      </>
    ) : null;

  return (
    <KlantkaartV10
      // Een andere klant: alles opnieuw, ook een open AVG-kaart (die mag
      // nooit voor de volgende klant blijven staan). Sessie 161: ook een
      // andere zaak begint opnieuw, op de standaardtab.
      key={`${zaakId}-${kaart.id}`}
      tabOnthouden={false}
      kaart={kaart}
      avgKnop={!voorstel && zaak?.klant_id === kaart.id}
      belTerugval={zaak?.bel?.nummer ?? null}
      acties={{ nieuw: startNieuw, bezig: nieuw.bezig }}
      // Sessie 72 (punt 7): de naam klikt door naar de klant; niet bij een
      // voorstel, dan is de klant nog niet gekoppeld.
      naamLink={zaak?.klant_id === kaart.id}
      onderregel={
        voorstel ? null : gekoppeldOp ? `gekoppeld op ${gekoppeldOp}` : null
      }
      kopRechts={sluitKnop}
      tussen={tussen}
      contactExtra={contactExtra}
      onGewist={() => navigate("/klanten")}
    />
  );
}

/**
 * "Klant koppelen" in het klantvlak (sessie 73 blok 4), zoals in de
 * belstand; werkt ook bij een afgeronde zaak. Na koppelen blijft een regel
 * met "Naar de kaart".
 */
function KlantKoppelen({ zaakId }: { zaakId: string }) {
  const [open, setOpen] = useState(false);
  const [gekoppeld, setGekoppeld] = useState<Gekoppeld | null>(null);

  if (gekoppeld) {
    return (
      <div className="rk-koppel" role="status">
        <span>
          Gekoppeld aan <b>{gekoppeld.naam}</b>
        </span>
        <Link to={`/klanten/${gekoppeld.klantId}`}>Naar de kaart</Link>
      </div>
    );
  }
  if (!open) {
    return (
      <div className="rk-koppel">
        <button type="button" className="v6-knop" onClick={() => setOpen(true)}>
          <Icoon naam="klant" klein />
          Klant koppelen
        </button>
      </div>
    );
  }
  return (
    <KlantKoppelVlak
      stijl="vlak"
      zaakId={zaakId}
      onSluit={() => setOpen(false)}
      onGekoppeld={(g) => {
        setGekoppeld(g);
        setOpen(false);
      }}
    />
  );
}
